/**
 * buddyLines — 伴侣情境微文案库（工单 5）
 * 5 条文案原文照用（含标点与 CTA 方括号段），按状态组织成常量表。
 * CTA 链接为官方地址。
 */

/** 情绪/情境状态（对话状态机的触发源） */
export type BuddyMood =
  | "onload"
  | "hover-kernelhub"
  | "hover-vault"
  | "poke-spam"
  | "idle";

/** 气泡内可点击 CTA（对应文案末尾的 [label] 方括号段） */
export interface BuddyCta {
  /** 方括号内标签原文，如 "调阅神枢 ->" */
  label: string;
  /** target="_blank" 直达地址 */
  href: string;
}

/**
 * 气泡行 id：情境 mood，或 "custom"（xin:bubble 事件注入的自定义文案）。
 * 文案库只感知三件事：伴侣自身状态、神枢 CLI 审批事件、iCloser 金库状态；
 * 全站唯一智能体是【信（XIN）】。
 */
export type BuddyLineId = BuddyMood | "custom";

export interface BuddyLine {
  id: BuddyLineId;
  /** 原文文案（含 [CTA] 方括号段，一字不改） */
  text: string;
  /** 文案末尾的行动引导，可点击直达 */
  cta?: BuddyCta;
  /** 气泡头行动效/情境短标签 */
  action: string;
}

export const BUDDY_LINES: Record<BuddyMood, BuddyLine> = {
  onload: {
    id: "onload",
    text: "哔哔！神经信标连接成功！我是信（XIN），你的随身 Agent 哨兵。随时准备跨端巡逻！",
    action: "降落入场",
  },
  "hover-kernelhub": {
    id: "hover-kernelhub",
    text: "离开工位心慌？挂上神枢通道，去星巴克也能在手机上批准那行致命的 rm -rf！[调阅神枢 ->]",
    cta: {
      label: "调阅神枢 ->",
      href: "https://www.icloser.xyz/kernelhub.html",
    },
    action: "掏出像素对讲机",
  },
  "hover-vault": {
    id: "hover-vault",
    text: "不带盘缠怎么当赛博骑士？多链资产、美元账户与全球 Visa 刷卡已就绪！[检视行囊 ->]",
    cta: {
      label: "检视行囊 ->",
      href: "https://icloser.xyz",
    },
    action: "高举黄金宝箱",
  },
  "poke-spam": {
    id: "poke-spam",
    text: "喂！编译还在跑，代码还没写完，一直戳我是不会凭空变出零 Bug 架构的！",
    action: "叉腰怒目 >_<",
  },
  idle: {
    id: "idle",
    text: "呼……宿主掉线了？终端连接已保持保活状态，放心走开吧。",
    action: "打瞌睡 Zzz",
  },
};

/** 各情境气泡驻留时长（到点淡出，同情境续期不重打字） */
export const LINE_DURATION_MS: Record<BuddyMood, number> = {
  onload: 8_000,
  "hover-kernelhub": 7_000,
  "hover-vault": 7_000,
  "poke-spam": 5_000,
  idle: 9_000,
};

/** 闲置判定：无操作 15s 触发打瞌睡 */
export const IDLE_MS = 15_000;

/** 打字机吐字速度：30ms/字 */
export const TYPE_MS_PER_CHAR = 30;

/** xin:bubble 事件（神枢 CLI 审批事件通道）的展示时长，约 3s 后回 idle */
export const CUSTOM_LINE_DURATION_MS = 3_000;

/** 由 xin:bubble 事件 detail.text 构造自定义气泡行 */
export function makeCustomLine(text: string): BuddyLine {
  return { id: "custom", text, action: "神枢回传" };
}
